import {
  Atom,
  Download,
  Link2,
  FileQuestion,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  TriangleAlert,
  X,
} from "lucide-react";
import { lazy, Suspense, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWorkspaceFileAccess } from "./WorkspaceFileAccess";

import { referenceWorkspaceFile } from "./api";
import type { FileVersion } from "@rnavoyager/contracts";
import { FileEvidencePanel, fileEvidence } from "./FileEvidence";
import { molecularStructureSource } from "./molecularStructureFormats";
import type { TaskEvent, WorkspaceFileEntry, WorkspaceFilePreview } from "./types";

const MolecularStructureViewer = lazy(() => import("./MolecularStructureViewer"));

export type WorkspacePreviewKind = "structure" | "image" | "text" | "unsupported";

export function resolveWorkspacePreviewKind(entry: WorkspaceFileEntry): WorkspacePreviewKind {
  if (entry.kind !== "file") return "unsupported";
  if (molecularStructureSource(entry.name)) return "structure";
  if (entry.previewable && entry.media_type?.startsWith("image/")) return "image";
  if (entry.previewable) return "text";
  return "unsupported";
}

export function WorkspaceFilePreviewDialog({
  entry,
  onClose,
  sessionId,
  events,
  onReference,
  referenceDisabledReason,
}: {
  entry: WorkspaceFileEntry;
  onClose: () => void;
  sessionId: string;
  events?: readonly TaskEvent[];
  onReference?: (file: FileVersion) => void;
  referenceDisabledReason?: string;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { downloadUrl, contentUrl } = useWorkspaceFileAccess();
  const dialogRef = useRef<HTMLElement>(null);
  const titleId = useId();
  const kind = resolveWorkspacePreviewKind(entry);
  const [view, setView] = useState<"preview" | "evidence">("preview");
  const hasEvidence = useMemo(
    () => Boolean(events && fileEvidence(events, entry.path).rows.length > 0),
    [entry.path, events],
  );
  useEffect(() => {
    if (!hasEvidence) setView("preview");
  }, [hasEvidence]);
  useEffect(() => setView("preview"), [entry.path]);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const handleDialogKeys = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          "a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), " +
            'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1) ?? first;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleDialogKeys);
    return () => {
      window.removeEventListener("keydown", handleDialogKeys);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="workspace-preview-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        ref={dialogRef}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`workspace-preview-dialog ${kind}`}
        role="dialog"
      >
        <header className="workspace-preview-header">
          <div className="workspace-preview-title">
            <PreviewKindIcon kind={kind} />
            <div>
              <div className="workspace-preview-heading-row">
                <h2 id={titleId} title={entry.name}>
                  {entry.name}
                </h2>
                <span>{previewKindLabel(kind)}</span>
              </div>
              <p title={entry.path}>{entry.path}</p>
              <small>
                {formatWorkspaceFileSize(entry.size ?? 0)}
                {entry.media_type ? ` · ${entry.media_type}` : ""}
                {entry.modified_at ? ` · ${formatModifiedAt(entry.modified_at)}` : ""}
              </small>
            </div>
          </div>
          <div className="workspace-preview-actions">
            {onReference && entry.kind === "file" && (
              <WorkspaceFileReferenceButton
                key={`${sessionId}:${entry.path}`}
                sessionId={sessionId}
                path={entry.path}
                disabledReason={referenceDisabledReason}
                onReference={onReference}
              />
            )}
            <a
              className="secondary"
              download={entry.name}
              href={downloadUrl(sessionId, entry.path)}
              title={`下载 ${entry.name}`}
            >
              <Download size={15} /> 下载
            </a>
            <button
              ref={closeButtonRef}
              aria-label="关闭文件预览"
              className="icon-button framed"
              type="button"
              onClick={onClose}
              title="关闭"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        {hasEvidence && (
          <nav aria-label="文件视图" className="workspace-preview-tabs" role="tablist">
            <button
              aria-selected={view === "preview"}
              className={view === "preview" ? "selected" : ""}
              role="tab"
              type="button"
              onClick={() => setView("preview")}
            >
              预览
            </button>
            <button
              aria-selected={view === "evidence"}
              className={view === "evidence" ? "selected" : ""}
              role="tab"
              type="button"
              onClick={() => setView("evidence")}
            >
              来源与核验
            </button>
          </nav>
        )}

        <main className="workspace-preview-body">
          {view === "evidence" && events ? (
            <FileEvidencePanel entry={entry} events={events} sessionId={sessionId} />
          ) : null}
          {view === "preview" && kind === "structure" && (
            <Suspense fallback={<PreviewLoading label="正在加载三维查看器…" />}>
              <MolecularStructureViewer
                key={entry.path}
                name={entry.name}
                url={downloadUrl(sessionId, entry.path)}
              />
            </Suspense>
          )}
          {view === "preview" && kind === "image" && (
            <WorkspaceImagePreview name={entry.name} url={contentUrl(sessionId, entry.path)} />
          )}
          {view === "preview" && kind === "text" && (
            <WorkspaceTextPreview entry={entry} sessionId={sessionId} />
          )}
          {view === "preview" && kind === "unsupported" && <UnsupportedPreview entry={entry} />}
        </main>
      </section>
    </div>,
    document.body,
  );
}

function WorkspaceFileReferenceButton({
  sessionId,
  path,
  disabledReason,
  onReference,
}: {
  sessionId: string;
  path: string;
  disabledReason?: string;
  onReference: (file: FileVersion) => void;
}) {
  const controller = useRef<AbortController | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => controller.current?.abort(), []);
  async function reference() {
    if (controller.current || disabledReason) return;
    const operation = new AbortController();
    controller.current = operation;
    setWorking(true);
    setError("");
    try {
      const file = await referenceWorkspaceFile(sessionId, path, operation.signal);
      if (!operation.signal.aborted) onReference(file);
    } catch (cause) {
      if (!operation.signal.aborted)
        setError(cause instanceof Error ? cause.message : "文件核验失败，未添加引用");
    } finally {
      if (!operation.signal.aborted) setWorking(false);
      if (controller.current === operation) controller.current = null;
    }
  }
  return (
    <div className="workspace-file-reference-control">
      <button
        className="secondary"
        type="button"
        disabled={working || Boolean(disabledReason)}
        title={disabledReason || "流式核验后引用现有文件，不重新运行工具"}
        onClick={() => void reference()}
      >
        {working ? <LoaderCircle className="spin" size={15} /> : <Link2 size={15} />}
        {working ? "正在核验…" : "引用此文件"}
      </button>
      {error && <span role="alert">{error}</span>}
    </div>
  );
}

function WorkspaceTextPreview({
  entry,
  sessionId,
}: {
  entry: WorkspaceFileEntry;
  sessionId: string;
}) {
  const [preview, setPreview] = useState<WorkspaceFilePreview | null>(null);
  const { preview: previewFile } = useWorkspaceFileAccess();
  const [error, setError] = useState("");
  const [view, setView] = useState<"rendered" | "source">("rendered");

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setPreview(null);
    setError("");
    setView("rendered");
    void previewFile(sessionId, entry.path, controller.signal)
      .then((result) => {
        if (active) setPreview(result);
      })
      .catch((cause: unknown) => {
        if (!active || (cause instanceof DOMException && cause.name === "AbortError")) return;
        setError(cause instanceof Error ? cause.message : "暂时无法读取文件内容。");
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [entry.path, sessionId, previewFile]);

  if (error) {
    return (
      <div className="workspace-preview-message" role="alert">
        <TriangleAlert size={24} />
        <strong>文件预览失败</strong>
        <span>{error}</span>
      </div>
    );
  }
  if (!preview) return <PreviewLoading label="正在读取文件内容…" />;

  const markdown = /\.(md|markdown)$/i.test(entry.name);
  const content = formatTextPreview(preview);
  return (
    <div className="workspace-text-preview">
      {(preview.truncated || markdown) && (
        <div className="workspace-text-preview-toolbar">
          {preview.truncated ? (
            <span>
              文件较大，仅展示前 {formatWorkspaceFileSize(utf8ByteLength(preview.content))}
            </span>
          ) : (
            <span />
          )}
          {markdown && (
            <div aria-label="Markdown 预览方式" className="workspace-preview-tabs">
              <button
                className={view === "rendered" ? "active" : ""}
                type="button"
                onClick={() => setView("rendered")}
              >
                渲染
              </button>
              <button
                className={view === "source" ? "active" : ""}
                type="button"
                onClick={() => setView("source")}
              >
                源文件
              </button>
            </div>
          )}
        </div>
      )}
      {markdown && view === "rendered" ? (
        <article className="workspace-markdown-preview">
          <ReactMarkdown
            components={{
              a: ({ children, ...props }) => (
                <a {...props} rel="noreferrer" target="_blank">
                  {children}
                </a>
              ),
              img: ({ alt }) => (
                <span className="workspace-markdown-image-note">[图片：{alt || "未命名"}]</span>
              ),
            }}
            remarkPlugins={[remarkGfm]}
          >
            {preview.content}
          </ReactMarkdown>
        </article>
      ) : (
        <pre className="workspace-source-preview">
          <code>{content}</code>
        </pre>
      )}
    </div>
  );
}

function WorkspaceImagePreview({ name, url }: { name: string; url: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  if (failed) {
    return (
      <div className="workspace-preview-message" role="alert">
        <TriangleAlert size={24} />
        <strong>图片加载失败</strong>
        <span>图片可能已被更新或格式不受浏览器支持。</span>
      </div>
    );
  }
  return (
    <div className="workspace-image-preview">
      <img alt={`${name} 文件预览`} src={url} onError={() => setFailed(true)} />
    </div>
  );
}

function UnsupportedPreview({ entry }: { entry: WorkspaceFileEntry }) {
  return (
    <div className="workspace-preview-message">
      <FileQuestion size={30} />
      <strong>暂不支持在线预览此格式</strong>
      <span>
        {entry.kind === "symlink"
          ? "为避免跨越会话工作区边界，符号链接只能显示元数据。"
          : "你仍然可以下载原始文件并使用本地应用打开。"}
      </span>
    </div>
  );
}

function PreviewLoading({ label }: { label: string }) {
  return (
    <div aria-live="polite" className="workspace-preview-message loading">
      <LoaderCircle className="spin" size={22} />
      <span>{label}</span>
    </div>
  );
}

function PreviewKindIcon({ kind }: { kind: WorkspacePreviewKind }) {
  if (kind === "structure") return <Atom aria-hidden="true" size={21} />;
  if (kind === "image") return <ImageIcon aria-hidden="true" size={21} />;
  if (kind === "text") return <FileText aria-hidden="true" size={21} />;
  return <FileQuestion aria-hidden="true" size={21} />;
}

function previewKindLabel(kind: WorkspacePreviewKind) {
  if (kind === "structure") return "三维结构";
  if (kind === "image") return "图片";
  if (kind === "text") return "文本";
  return "文件";
}

function formatTextPreview(preview: WorkspaceFilePreview) {
  if (preview.truncated || preview.media_type !== "application/json") return preview.content;
  try {
    return JSON.stringify(JSON.parse(preview.content), null, 2);
  } catch {
    return preview.content;
  }
}

function formatModifiedAt(value: string) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}

export function formatWorkspaceFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function utf8ByteLength(value: string) {
  return new TextEncoder().encode(value).byteLength;
}
